<template>
  <v-navigation-drawer
    v-model="state.open"
    right
    temporary
    app
    class="pay-plan-version-pane-connected"
    width="min(1100px, 92vw)"
  >
    <div
      v-if="state.error"
      class="ppvpc__error"
      data-testid="ppvpc-inline-error"
      role="alert"
    >
      {{ state.error }}
    </div>

    <!-- ECL pane (ER-12119): loading / future-scheduled / voided -->
    <PayPlanVersionPane
      v-else
      :scenario="scenario"
      :version="drawerVersion"
      :fair-pay-at-effective-date="fairPayAtEffectiveDate"
      :readiness-enabled="readinessEnabled"
      :voidable="voidable"
      :void-blocked-by="voidBlockedBy"
      @void="openVoid"
      @propose-changes="
        emit('propose-changes', { versionId: state.versionId, version })
      "
      @open-chatter="
        emit('open-chatter', { id: state.versionId, versionCode: version?.code })
      "
      @close="close"
    />

    <!-- Void confirmation (Rule B-14: irreversible, reason gates confirm) -->
    <PayPlanDialog
      :open="state.voidOpen"
      title="Void pay plan version"
      :submitting="state.submitting"
      @close="cancelVoid"
    >
      <template #icon>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 6.667v3.333m0 3.333h.008M8.575 2.283 1.517 14.167a1.667 1.667 0 0 0 1.425 2.5h14.116a1.667 1.667 0 0 0 1.425-2.5L11.425 2.283a1.667 1.667 0 0 0-2.85 0Z"
            stroke="#d92d20"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>

      <template #description>
        This action is irreversible. Voiding
        <strong>{{ version?.code || "this version" }}</strong> permanently voids this pay plan
        version. A reason is required.
      </template>

      <label class="ppvpc__field-label" for="ppvpc-void-reason">Reason</label>
      <v-textarea
        id="ppvpc-void-reason"
        v-model="state.voidReason"
        data-testid="ppvpc-void-reason"
        rows="3"
        auto-grow
        hide-details
        outlined
        dense
      />

      <template #footer>
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          data-testid="ppvpc-void-cancel"
          :disabled="state.submitting"
          @click="cancelVoid"
        />
        <StandardButton
          type="red"
          variant="primary"
          size="normal"
          label="Void"
          data-testid="ppvpc-void-confirm"
          :disabled="!state.voidReason.trim() || state.submitting || featureDisabled"
          :loader-left="state.submitting"
          @click="voidVersion"
        />
      </template>
    </PayPlanDialog>
  </v-navigation-drawer>
</template>

<script setup>
import { computed } from "vue";
import PayPlanDialog from "@components/PayPlanDialog/PayPlanDialog.vue";
import PayPlanVersionPane from "@components/PayPlanVersionPane/PayPlanVersionPane.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { usePayPlanVersionPane } from "./composables/usePayPlanVersionPane";

const props = defineProps({
  proxy: {
    type: Object,
    default: null,
  },
  fetchRows: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["open-chatter", "propose-changes"]);

const {
  state,
  scenario,
  version,
  fairPayAtEffectiveDate,
  readinessEnabled,
  voidable,
  voidBlockedBy,
  featureDisabled,
  open,
  close,
  openVoid,
  cancelVoid,
  voidVersion,
} = usePayPlanVersionPane({ proxy: props.proxy, fetchRows: props.fetchRows });

defineExpose({ open, close });

/** The ECL requires `version`; supply a blank shell while the fetch is running. */
const drawerVersion = computed(
  () =>
    version.value || {
      code: "",
      status: "In Force",
      effective_from: null,
      effective_to: null,
      country: null,
      currency: null,
      amount: null,
      pay_basis: null,
      fte: null,
      employee: "",
      employee_title: "",
      avatar_url: null,
    }
);
</script>

<style scoped>
.ppvpc__error {
  margin: 16px;
  padding: 12px 16px;
  border: 1px solid #fda29b;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.ppvpc__dialog-copy {
  margin: 0 0 12px;
  color: #344054;
  font-size: 14px;
  line-height: 20px;
}

.ppvpc__dialog-title {
  color: #101828;
  font-size: 16px;
  font-weight: 600;
}

.ppvpc__field-label {
  margin-bottom: 6px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ppvpc__dialog-actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
</style>
